import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'store/store';
import {
	addChangesHistory,
	createTicket,
	getTicketsByUserId,
	searchTickets,
} from 'store/ticket-service/asyncActions';
import {
	getActiveTicket,
	getIsReportAnIssueMode,
	getIsSummaryLoading,
	getIsTicketShare,
	getIsTicketUpdate,
	getIsTyping,
	getTicketUpdating,
	getTickets,
	getTicketsLoader,
	getTicketsLoadingMore,
} from 'store/ticket-service/selector';
import {
	addLocalTicket,
	setActiveTicket,
	setIsStepperSubStagesOpen,
	setSidebarView,
} from 'store/ticket-service/ticketSlice';
import { TicketType } from 'store/ticket-service/types';
import { getIsCreateTicketShare } from 'store/ticket-share-service/selector';
import { setIsCreateTicketShareOpen } from 'store/ticket-share-service/ticketShareSlice';
import { getIsOpenUserProfile } from 'store/user-service/selector';
import { setIsOpenUserProfile } from 'store/user-service/userSlice';

import {
	GeneralStepConversationTopics,
	HistoryActionTypes,
	MessageSender,
	NamespacesList,
	TicketStages,
} from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';
import useDebounce from 'core/hooks/useDebounce';

import { IMessage } from 'components/Chatbot/Message/types';
import { SidebarTicket } from 'components/Sidebar/SidebarTicket';
import { CreateTicketModal } from 'components/Ticket/CreateTicketModal';

import { ReactComponent as Add } from 'assets/icons/add.svg';
import { ReactComponent as ArrowLeft } from 'assets/icons/light-blue-small-arrow-left.svg';
import { ReactComponent as Search } from 'assets/icons/search.svg';
import { ReactComponent as ArrowRight } from 'assets/icons/small-arrow-right.svg';
import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

import * as Styled from './styled';

interface SidebarProps {
	isShowDraft: boolean;
	setIsShowDraft: (arg0: boolean) => void;
	isCreating: boolean;
	setIsCreating: (arg0: boolean) => void;
}

export const Sidebar: FC<SidebarProps> = ({
	isShowDraft,
	setIsShowDraft,
	isCreating,
	setIsCreating,
}) => {
	const dispatch = useAppDispatch();
	const tickets = useSelector(getTickets);
	const loader = useSelector(getTicketsLoader);
	const activeTicket = useSelector(getActiveTicket);
	const isTicketUpdate = useSelector(getIsTicketUpdate);
	const loadingMore = useSelector(getTicketsLoadingMore);
	const isTyping = useSelector(getIsTyping);
	const isSummaryLoading = useSelector(getIsSummaryLoading);
	const reportAnIssueMode = useSelector(getIsReportAnIssueMode);
	const isOpenUserProfile = useSelector(getIsOpenUserProfile);
	const isUserProfileOpen = useSelector(getIsOpenUserProfile);
	const isTicketUpdating = useSelector(getTicketUpdating);
	const isTicketShare = useSelector(getIsTicketShare);
	const isTicketShareModalOpen = useSelector(getIsCreateTicketShare);

	const { t: commonTranslate, i18n } = useTranslation(NamespacesList['common']);

	const [searchQuery, setSearchQuery] = useState('');
	const [totalTickets, setTotalTickets] = useState<number>(0);
	const [totalFilteredTickets, setTotalFilteredTickets] = useState<number>(0);
	const [isSidebarHidden, setIsSidebarHidden] = useState(false);
	const [isSharedTicketsOpen, setIsSharedTicketsOpen] = useState<boolean>(true);
	const [isOwnTicketsOpen, setIsOwnTicketsOpen] = useState<boolean>(true);

	const sharedTickets = useMemo(
		() => tickets.filter((ticket) => ticket.permissions.length),
		[tickets]
	);

	const ownTickets = useMemo(
		() => tickets.filter((ticket) => !ticket.permissions.length),
		[tickets]
	);

	const debouncedSearchQuery = useDebounce(searchQuery, 200);

	const handleCloseModal = () => {
		setIsShowDraft(false);
	};

	const storedUser = localStorage.getItem('user');
	const user = storedUser ? JSON.parse(storedUser) : null;

	const errorSearch = () => {
		toast.error(
			<div>
				<Typography fontWeight="700" color={titleTextColor}>
					{commonTranslate('errors.error')}
				</Typography>
				<Typography color={regularTextColor}>
					{commonTranslate('errors.ticketsNotFound')}
				</Typography>
			</div>,
			{
				autoClose: 1500,
				hideProgressBar: true,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				theme: 'light',
				style: {
					borderLeft: '8px solid #dc3545',
				},
			}
		);
	};

	useEffect(() => {
		if (debouncedSearchQuery.length) {
			dispatch(searchTickets({ userId: user.id, ticketTitle: debouncedSearchQuery })).then(
				(response) => {
					!response.payload.data.length && errorSearch();
					setTotalFilteredTickets(response?.payload?.totalRecordsFound);
				}
			);
			currentPageRef.current = 1;
			const container = containerRef.current;
			if (container) {
				container.scrollTop = 0;
			}
		}
	}, [debouncedSearchQuery]);

	useEffect(() => {
		const activeTicketIsInTicketsList = Boolean(
			tickets.find((ticket) => ticket?.id === activeTicket?.id)
		);

		const hasNoValidActiveTicket = !activeTicket || !activeTicketIsInTicketsList;

		if (tickets.length > 0 && hasNoValidActiveTicket && !isOpenUserProfile) {
			dispatch(setActiveTicket(tickets[0]));
			dispatch(setIsOpenUserProfile(false));
			dispatch(setIsCreateTicketShareOpen(false));
		}
	}, [tickets, activeTicket, isOpenUserProfile]);

	const isTicketSaveAvailable = useMemo(() => {
		if (isTicketShare) {
			const ticketShareStagesPermissions =
				activeTicket?.permissions?.map((permission) => permission.ticketCurrentStep) || [];

			return (
				ticketShareStagesPermissions.includes(TicketStages.general) ||
				ticketShareStagesPermissions.includes(TicketStages.notifications)
			);
		}

		return true;
	}, [isTicketShare, activeTicket?.id]);

	const handleCloseUserProfile = () => {
		if (isUserProfileOpen) {
			dispatch(setIsOpenUserProfile(false));
		}
	};

	const handleShareModalClose = () => {
		if (isTicketShareModalOpen) dispatch(setIsCreateTicketShareOpen(false));
	};

	const createLocalTicket = () => {
		handleCloseUserProfile();
		handleShareModalClose();

		if (activeTicket) {
			if (!activeTicket.id) {
				setIsShowDraft(true);
				setIsCreating(true);
				return;
			}

			if (isTicketUpdate) {
				setIsShowDraft(true);
				setIsCreating(true);
				return;
			}
		}

		const welcomeMessage: IMessage = {
			content: `generalStep.welcomeMessage.${GeneralStepConversationTopics[0]}`,
			sender: MessageSender.System,
			sendTime: new Date().toISOString(),
			stage: TicketStages.general,
			subStage: GeneralStepConversationTopics['Functional requirements'],
			userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
		};

		const defaultTicket: Partial<TicketType> = {
			title: 'New request',
			description: '',
			createdDate: new Date().toISOString(),
			comment: '',
			messages: [welcomeMessage],
			status: 0,
			currentStep: TicketStages.general,
			ticketSummaries: [],
			userStoryTests: [],
			useCases: [],
			notifications: [],
		};

		dispatch(createTicket({ userId: user.id, ticket: defaultTicket })).then((data: any) => {
			dispatch(addLocalTicket(data.payload));
			dispatch(setActiveTicket(data.payload));

			dispatch(
				addChangesHistory({
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
					ticketId: data.payload.id,
					ticketStep: TicketStages.general,
					actionType: HistoryActionTypes.ticketCreate,
				})
			);
		});
	};

	const handleTicketClick = (ticket: TicketType) => {
		if (window.innerWidth < 900) {
			dispatch(setSidebarView(false));
		}

		dispatch(setIsStepperSubStagesOpen(false));
		handleCloseUserProfile();
		handleShareModalClose();

		if (!isTyping && !isSummaryLoading) {
			if (reportAnIssueMode) {
				errorNotify(
					commonTranslate('errors.canNotSwitchTicketTitle'),
					commonTranslate('errors.canNotSwitchTicketDescription')
				);

				return;
			}

			if (isTicketUpdate && ticket.id !== activeTicket?.id && isTicketSaveAvailable) {
				setIsShowDraft(true);
				setIsCreating(false);
			} else {
				dispatch(setActiveTicket(ticket));
			}
		}
	};

	const containerRef = useRef<HTMLDivElement>(null);
	const currentPageRef = useRef<number>(1);

	useEffect(() => {
		const container = containerRef.current;
		if (!debouncedSearchQuery.length) {
			dispatch(getTicketsByUserId({ userId: user.id })).then((response) => {
				if (!searchQuery.length) {
					setTotalTickets(response?.payload?.totalRecordsFound);
				}
			});

			currentPageRef.current = 1;
			if (container) {
				container.scrollTop = 0;
			}
		}

		const handleScroll = async () => {
			if (container) {
				const isAtBottom =
					Math.floor(container.scrollHeight - container.scrollTop) === container.clientHeight;
				if (isAtBottom) {
					if (!debouncedSearchQuery.length) {
						const nextPage = currentPageRef.current + 1;
						dispatch(
							getTicketsByUserId({
								userId: user.id,
								currentPageNumber: nextPage,
							})
						)
							.then((action: any) => {
								const newTickets = action.payload.data;
								if (newTickets.length > 0) {
									currentPageRef.current = nextPage;
								}
							})
							.catch((error: any) => {
								console.log(error);
							});
					}

					if (debouncedSearchQuery.length) {
						const nextPage = currentPageRef.current + 1;
						dispatch(
							searchTickets({
								userId: user.id,
								ticketTitle: debouncedSearchQuery,
								currentPageNumber: nextPage,
							})
						)
							.then((action: any) => {
								const newTickets = action.payload.data;
								if (newTickets.length > 0) {
									currentPageRef.current = nextPage;
								}
							})
							.catch((error: any) => {
								console.log(error);
							});
					}
				}
			}
		};

		container?.addEventListener('scroll', handleScroll);
		return () => {
			container?.removeEventListener('scroll', handleScroll);
		};
	}, [debouncedSearchQuery]);

	return (
		<>
			<Box
				className="rtl-able"
				sx={{
					position: 'relative',
					display: 'flex',
					width: isSidebarHidden ? '0px' : 'clamp(300px, 25%, 350px)',
					padding: isSidebarHidden ? '10px' : '20px',
					flexDirection: 'column',
					alignItems: { xs: 'center', md: 'end' },
					justifyContent: 'space-between',
					backgroundColor: '#FBFBFB',
					borderRight: i18n.language === 'ar' ? 'none' : '2px solid #E7E8EC',
					borderLeft: i18n.language === 'ar' ? '2px solid #E7E8EC' : 'none',
					boxSizing: 'border-box',
				}}
			>
				{!isSidebarHidden ? (
					<Box
						sx={{
							width: '100%',
							alignItems: { xs: 'center', md: 'unset' },
							display: 'flex',
							flexDirection: 'column',
							gap: '24px',
							maxHeight: '100%',
						}}
					>
						{loader ? (
							<Button
								sx={{
									display: { xs: 'none', md: 'flex' },
									height: '44px',
									width: '100%',
									borderRadius: '8px',
									padding: '8px 16px',
								}}
								variant="contained"
								color="primary"
							>
								<Oval
									height={24}
									width={24}
									color="#fff"
									wrapperStyle={{}}
									wrapperClass=""
									visible={true}
									ariaLabel="oval-loading"
									secondaryColor="#91B6FF"
									strokeWidth={5}
									strokeWidthSecondary={5}
								/>
							</Button>
						) : (
							<Button
								sx={{
									display: { xs: 'none', md: 'flex' },
									height: '44px',
									width: '100%',
									borderRadius: '8px',
									textTransform: 'none',
									fontSize: '14px',
									fontWeight: '700',
									gap: '8px',
									padding: '8px 16px',
									backgroundColor: '#4786FF',
								}}
								variant="contained"
								color="primary"
								disabled={isTicketUpdating}
								onClick={createLocalTicket}
							>
								<Add />
								{commonTranslate('buttons.createNewRequest')}
							</Button>
						)}

						<TextField
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search />
									</InputAdornment>
								),
								style: {
									paddingLeft: '12px',
									height: '44px',
									borderRadius: '8px',
									background: '#F3F4F5',
								},
							}}
							sx={{
								border: 'none',
								height: '44px',
								width: '100%',
								textTransform: 'none',
								fontSize: '16px',
								fontWeight: '700',
								gap: '8px',

								'& fieldset': { border: 'none' },
								'& input::placeholder': { color: '#8790A0', opacity: 1 },
							}}
							placeholder={commonTranslate('inputs.searchInputPlaceholder') as string}
							variant="outlined"
							color="primary"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>

						<Box
							ref={containerRef}
							sx={{
								width: '100%',
								alignItems: { xs: 'center', md: 'unset' },
								display: 'flex',
								flexDirection: 'column',
								gap: '16px',
								overflowY: 'auto',
								overflowX: 'hidden',
							}}
						>
							{sharedTickets.length ? (
								<Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
									<Box
										onClick={() => setIsSharedTicketsOpen(!isSharedTicketsOpen)}
										sx={{ padding: '4px' }}
									>
										<ArrowRight
											style={{ transform: `rotate(${isSharedTicketsOpen ? '90deg' : '0deg'})` }}
										/>
									</Box>
									<Typography color={regularTextColor} sx={{ fontSize: '14px' }}>
										{commonTranslate('text.sharedTickets')}:
									</Typography>
								</Box>
							) : null}
							{isSharedTicketsOpen
								? sharedTickets.map((ticket: TicketType, index: number) => (
										<SidebarTicket
											key={JSON.stringify(ticket) + index + (ticket?.id || '')}
											ticket={ticket}
											handleTicketClick={handleTicketClick}
										/>
								  ))
								: null}
							{ownTickets.length && sharedTickets.length ? (
								<Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
									<Box
										onClick={() => setIsOwnTicketsOpen(!isOwnTicketsOpen)}
										sx={{ padding: '4px' }}
									>
										<ArrowRight
											style={{ transform: `rotate(${isOwnTicketsOpen ? '90deg' : '0deg'})` }}
										/>
									</Box>
									<Typography color={regularTextColor}>
										{commonTranslate('text.ownTickets')}:
									</Typography>
								</Box>
							) : null}
							{isOwnTicketsOpen
								? ownTickets.map((ticket: TicketType, index: number) => (
										<SidebarTicket
											key={JSON.stringify(ticket) + index + (ticket?.id || '')}
											ticket={ticket}
											handleTicketClick={handleTicketClick}
										/>
								  ))
								: null}
							<></>
						</Box>
					</Box>
				) : null}

				<Styled.FocusModeButton
					onClick={() => setIsSidebarHidden(!isSidebarHidden)}
					sx={{
						left: i18n.language === 'ar' ? '-14px' : 'calc(100% - 12px)',
						transform: `rotate(${i18n.language === 'ar' ? '180deg' : '0deg'})`,
					}}
				>
					<ArrowLeft style={{ transform: `rotate(${isSidebarHidden ? '180deg' : '0deg'})` }} />
				</Styled.FocusModeButton>

				{debouncedSearchQuery &&
					tickets.length > 0 &&
					tickets.length < totalFilteredTickets &&
					loadingMore && (
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{
								alignSelf: 'center',
							}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					)}

				{!debouncedSearchQuery &&
					tickets.length > 0 &&
					tickets.length < totalTickets &&
					loadingMore && (
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{
								alignSelf: 'center',
							}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					)}

				{loader ? (
					<Button
						sx={{
							display: { xs: 'flex', md: 'none' },
							height: '44px',
							width: '100%',
							borderRadius: '8px',
							padding: '8px 16px',
						}}
						variant="contained"
						color="primary"
					>
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Button>
				) : (
					<Button
						sx={{
							display: { xs: 'flex', md: 'none' },
							height: '44px',
							width: '100%',
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
							gap: '8px',
							padding: '8px 16px',
						}}
						variant="contained"
						color="primary"
						disabled={isTicketUpdating}
						onClick={createLocalTicket}
					>
						<Add />
						{commonTranslate('buttons.createNewRequest')}
					</Button>
				)}
			</Box>

			{isShowDraft && activeTicket && (
				<CreateTicketModal
					handleCloseModal={handleCloseModal}
					ticket={activeTicket}
					userId={user.id}
					isCreating={isCreating}
				/>
			)}
		</>
	);
};
